import { SessionService } from '../Session/SessionService';
import { ConfigService } from '../Configs/ConfigService';
import { PreferenceService } from '../Preferences/PreferenceService';
import { ConsoleLogger, NoLogger } from './Loggers';
import { LoggerInterface, LogData, LoggerData, LogLevels } from './types/LoggerTypes';

class Logger implements LoggerInterface {
  constructor(loggersContext?: LoggerData) {
    this.loggerObj = {
      product: '',
      component: '',
      sessionId: SessionService.getSession()?.session?.id,
      ...loggersContext,
    };

    this.log = (() => this.callOrReturnLog('log'))();
    this.info = (() => this.callOrReturnLog('info'))();
    this.warn = (() => this.callOrReturnLog('warn'))();
    this.debug = (() => this.callOrReturnLog('debug'))();
    this.error = (() => this.callOrReturnLog('error'))();
  }

  private static loggersArr: LoggerInterface[] = [ConsoleLogger, NoLogger];
  private loggerObj: LoggerData;
  log: (...data: LogData[] | LoggerInterface[]) => void;
  info: (...data: LogData[] | LoggerInterface[]) => void;
  warn: (...data: LogData[] | LoggerInterface[]) => void;
  debug: (...data: LogData[] | LoggerInterface[]) => void;
  error: (...data: LogData[] | LoggerInterface[]) => void;

  /**
   * This method returns the logging level flag based on the logging method passed.
   */
  private logLevelsCondition = (method: keyof LoggerInterface) => {
    const configs = ConfigService.getConfigs();
    switch (method) {
      case 'info':
        return !configs.LOG_LEVEL || LogLevels[configs.LOG_LEVEL as keyof typeof LogLevels] <= LogLevels.INFO;
      case 'warn':
        return !configs.LOG_LEVEL || LogLevels[configs.LOG_LEVEL as keyof typeof LogLevels] <= LogLevels.WARN;
      case 'debug':
        return !configs.LOG_LEVEL || LogLevels[configs.LOG_LEVEL as keyof typeof LogLevels] <= LogLevels.DEBUG;
      case 'error':
        return !configs.LOG_LEVEL || LogLevels[configs.LOG_LEVEL as keyof typeof LogLevels] <= LogLevels.ERROR;
      case 'log':
      default:
        return true;
    }
  };

  /**
   * When devMode is set to true in PreferenceService, this method returns console methods directly to the caller and executes there to behave same as console methods should. i.e., displaying context information, seeing the source code on click of file and line number.
   * When devMode is false, this loops through loggers array and logs directly to them.
   */
  private callOrReturnLog = (method: keyof LoggerInterface) => {
    const { devMode } = PreferenceService.getPreferences();
    if (devMode) {
      // eslint-disable-next-line no-console
      return console[method];
    } else {
      return (...args: Array<LogData>) => {
        if (!this.logLevelsCondition(method)) {
          return;
        }
        const finalArr = args.flat();
        // Extracting Last Item as it could be a custom logger.
        const lastItem = finalArr[finalArr.length - 1] as LoggerInterface;
        const customLogger = lastItem && typeof lastItem === 'object' && method in lastItem;

        (customLogger ? [lastItem as LoggerInterface] : Logger.loggersArr).forEach((logger) => {
          logger[method](this.loggerObj, ...(customLogger ? finalArr.slice(0, finalArr.length - 1) : args));
        });
      };
    }
  };

  static setLoggers = (...args: Array<LoggerInterface>) => {
    Logger.loggersArr = [...args];
  };

  static getInstance(loggersData?: LoggerData): LoggerInterface {
    return new Logger(loggersData);
  }
}

export default Logger;
